/*
  <fade-carousel> — GB automatic one-at-a-time fade carousel.
  New code (this file only). Deferred, self-registering, no dependencies.

  Shows exactly one slide at a time and cross-fades to the next on a timer.
  Not a scroller: every slide occupies the same grid cell, so there is no
  horizontal track, no scroll snap and no layout shift. Container height is
  the tallest slide, resolved by CSS grid, not by JS measurement.

  Timing comes from the section: data-interval (ms between slides) and the
  --fade-carousel-duration custom property (length of the cross-fade).

  Stops rotating when:
  - the visitor asks for reduced motion (WCAG 2.3.3), first slide stays up
  - the section is scrolled out of view, so it burns nothing off-screen
  - the pointer is over it, when pause on hover is on
  - anything inside it has keyboard focus, which is the pause mechanism for
    keyboard and screen reader users (WCAG 2.2.2), since each card's button
    is focusable

  Theme editor: selecting an Ingredient Card block jumps straight to that
  card and holds it there, so merchants can edit a card without chasing it
  around the rotation. Deselecting resumes.
*/
if (!customElements.get('fade-carousel')) {
  customElements.define(
    'fade-carousel',
    class FadeCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.slides = Array.from(this.querySelectorAll('.fade-carousel__slide'));
        if (this.slides.length === 0) return;

        this.index = 0;
        this.interval = parseInt(this.dataset.interval, 10) || 0;
        this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
        this.held = false;
        this.visible = false;

        this.show(0);

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || this.interval <= 0 || this.slides.length < 2) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            this.visible ? this.start() : this.stop();
          },
          { rootMargin: '0px 0px 100px 0px' }
        );
        this.observer.observe(this);

        if (this.pauseOnHover) {
          this.addEventListener('mouseenter', () => this.stop());
          this.addEventListener('mouseleave', () => this.start());
        }
        // Focus pause is not optional. It is the keyboard equivalent of hover.
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());

        // A card showing its back must not rotate away mid-read. The flip
        // component announces itself; neither side needs to know the other.
        this.addEventListener('gb:card:flip-open', () => {
          this.held = true;
          this.stop();
        });
        this.addEventListener('gb:card:flip-close', () => {
          this.held = false;
          this.start();
        });

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      disconnectedCallback() {
        this.stop();
        this.observer?.disconnect();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      onBlockSelect = (event) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === event.detail.blockId);
        if (i === -1) return;
        this.held = true;
        this.stop();
        this.show(i);
      };

      onBlockDeselect = () => {
        this.held = false;
        this.start();
      };

      start() {
        if (this.timer || this.held || !this.visible || this.interval <= 0) return;
        this.timer = setInterval(() => this.show((this.index + 1) % this.slides.length), this.interval);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      show(i) {
        this.index = i;
        // Never leave a card flipped on a slide that is fading out — the next
        // time it comes round it would appear already showing its back.
        this.querySelectorAll('.ingredient-card--flipped').forEach((card) => {
          card.classList.remove('ingredient-card--flipped');
          card.querySelector('[data-ingredient-flip="open"]')?.setAttribute('aria-expanded', 'false');
        });
        this.slides.forEach((slide, n) => {
          const active = n === i;
          slide.classList.toggle('is-active', active);
          // Keeps the inactive cards out of the accessibility tree and out of
          // the tab order, so a keyboard user never lands on a hidden button.
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }
    }
  );
}
